(() => {
  'use strict';

  const KEY = '__NMRSTOR_PERFORMANCE_BRAIN_V2__';
  const VERSION = 'NMRSTOR PERFORMANCE BRAIN V2.14-FINAL';

  const FINAL_QA_PROFILE =
    Object.freeze({
      phase: 14,
      matrixPassed: 54,
      matrixTotal: 54,
      status: 'PASS',
      scope:
        'post-p2-release-closure',
      checkoutWarmup:
        'independent-preserved',
      legacyWarmupV1:
        'not-loaded',
      stage01LegacyPredictor:
        'inactive-fallback-preserved',
      postAuditRemediation:
        'P2-prediction-unified',
      stage01EndToEndPolicy:
        'PASS',
      productionConsole:
        'disabled',
      releaseClosure:
        'PASS'
    });

  if (window[KEY]) {
    window[KEY].warm?.('duplicate');
    return;
  }

  const connection =
    navigator.connection ||
    navigator.mozConnection ||
    navigator.webkitConnection ||
    null;

  const state = {
    startedAt: Date.now(),
    tier: 'standard',
    tierReason: 'initial',
    capabilityEvaluations: 0,
    capabilitySignals: null,
    pageLoaded:
      document.readyState === 'complete',
    loadAt:
      document.readyState === 'complete'
        ? Date.now()
        : 0,
    prerendering:
      document.prerendering === true,
    wasPrerenderingAtBoot:
      document.prerendering === true,
    activated:
      document.prerendering !== true,
    activationAt:
      document.prerendering === true
        ? 0
        : Date.now(),
    performanceReads: 0,
    navigationMetricsSupported: false,
    navigationEntryType: '',
    navigationActivationStart: 0,
    navigationDuration: 0,
    navigationResponseEnd: 0,
    navigationDomContentLoadedEnd: 0,
    navigationLoadEventEnd: 0,
    activatedFromPrerender: false,
    navigationMetricLastReason: '',
    navigationMetricLastReadAt: 0,
    pageshowCount: 0,
    bfcacheRestores: 0,
    pagehideCount: 0,
    bfcachePagehides: 0,
    prerenderActivationsObserved: 0,
    lifecyclePaused: false,
    lifecyclePauses: 0,
    lifecycleResumes: 0,
    lightRefreshes: 0,
    bfcacheLightRefreshes: 0,
    visibilityHiddenCount: 0,
    visibilityVisibleCount: 0,
    visibilityCooldownMs: 1500,
    visibilityCooldownSkips: 0,
    hiddenIntentCancellations: 0,
    hiddenSpeculationStops: 0,
    lastPauseAt: 0,
    lastPauseReason: '',
    lastResumeAt: 0,
    lastResumeReason: '',
    interactiveBound: false,
    heavyWarmStarted: false,
    heavyWarmAt: 0,
    heavyWarmReason: '',
    lifecycleBlocks: 0,
    headerWarmRuns: 0,
    headerWarmReady: false,
    headerWarmAt: 0,
    headerWarmReason: '',
    headerLastMode: '',
    headerLastTier: '',
    headerDecodedImages: 0,
    headerStyleAssets: new Set(),
    headerSurfacesTouched: 0,
    headerModeObserver: null,
    footerWarmRuns: 0,
    footerWarmReady: false,
    footerWarmAt: 0,
    footerWarmReason: '',
    footerDecodedImages: 0,
    footerImagesRequested: new Set(),
    footerSurfacesTouched: 0,
    footerObserver: null,
    footerObserved: false,
    footerObserverMargin: '',
    footerFallbackBound: false,
    idleRuns: 0,
    accountRuns: 0,
    accountReady: false,
    secondaryRequested: false,
    accountWarmRequests: 0,
    accountWarmSuccesses: 0,
    accountWarmFailures: 0,
    accountRewarms: 0,
    accountSecondaryArmed: false,
    accountLastRequestAt: 0,
    accountLastSuccessAt: 0,
    accountLastReason: '',
    accountLastSignature: '',
    accountObservedRoot: null,
    accountIntegrityTimer: 0,
    accountRewarmWindowAt: 0,
    accountRewarmsInWindow: 0,
    shellRuns: 0,
    decodedImages: 0,
    retainedImages: [],
    prefetched: new Set(),
    prerendered: new Set(),
    activeSpeculation: null,
    activePrerenderUrl: '',
    prefetchBudgetUrls: new Set(),
    prefetchRequests: 0,
    prerenderRequests: 0,
    prefetchLinksInserted: 0,
    prefetchFetchRequests: 0,
    prerenderRulesInserted: 0,
    prefetchBudgetRejected: 0,
    prerenderBudgetRejected: 0,
    cooldownRejected: 0,
    prerenderReplacements: 0,
    policyDecisions: 0,
    policyPrefetchDecisions: 0,
    policyPrerenderDecisions: 0,
    policyNoWarmDecisions: 0,
    policyDowngrades: 0,
    policyUnsupportedFallbacks: 0,
    policyFamilyCounts: {},
    lastNavigationPolicy: null,
    lastNetworkWarmAt: 0,
    lastNetworkWarmHref: '',
    shellIntentTimers: new WeakMap(),
    shellIntentOwners: new Set(),
    intentRecentSignals: new WeakMap(),
    intentDwellMs: 200,
    intentScheduled: 0,
    intentExecuted: 0,
    intentCancelled: 0,
    intentHighConfidence: 0,
    intentMediumConfidence: 0,
    lastIntent: '',
    lastIntentAt: 0,
    lastIntentConfidence: '',
    stage01UnifiedCalls: 0,
    stage01UnifiedPrefetchDecisions: 0,
    stage01UnifiedPrerenderDecisions: 0,
    stage01UnifiedNoWarmDecisions: 0,
    stage01LegacyBooleanCalls: 0,
    routeChecks: 0,
    routeBlocked: 0,
    routePrefetchOnly: 0,
    routePrerenderSafe: 0,
    routeUnknownBlocked: 0,
    routeLocalizedChecks: 0,
    routeBlockedSamples: [],
    lastRouteDecision: null,
    skipped: [],
    observer: null
  };

  const ROUTE_BLOCKED_PREFIXES =
    Object.freeze([
      '/checkout',
      '/checkouts',
      '/account',
      '/customer_authentication',
      '/apps',
      '/challenge',
      '/password',
      '/cart/add',
      '/cart/change',
      '/cart/update',
      '/cart/clear',
      '/localization',
      '/discount',
      '/wallets',
      '/payments',
      '/services/login_with_shop',
      '/admin',
      '/search/suggest',
      '/recommendations/products'
    ]);

  const ROUTE_PREFETCH_ONLY_PREFIXES =
    Object.freeze([
      '/cart',
      '/search'
    ]);

  const ROUTE_PRERENDER_SAFE_PREFIXES =
    Object.freeze([
      '/products',
      '/collections',
      '/pages',
      '/blogs',
      '/policies'
    ]);

  const ROUTE_NON_DOCUMENT_SUFFIXES =
    Object.freeze([
      '.js',
      '.json',
      '.xml',
      '.atom',
      '.rss',
      '.csv'
    ]);

  const ROUTE_MUTATION_QUERY_KEYS =
    Object.freeze([
      '_method',
      'form_type',
      'checkout',
      'discount',
      'section_id',
      'sections'
    ]);

  /*
    PHASE 1 CENTRAL PREDICTION BRIDGE
    Owns the legacy Stage01 prediction listeners and requests.
    Hardening/tier policy comes in later phases.
  */
  const stage01Prefetched =
    new Set();

  const stage01IntentTimers =
    new WeakMap();

  const stage01IntentAnchors =
    new Set();

  let stage01PredictorBound =
    false;

  function designMode() {
    return Boolean(
      window.Shopify?.designMode ||
      document.documentElement.hasAttribute('data-shopify-editor')
    );
  }

  const TIER_POLICIES =
    Object.freeze({
      constrained:
        Object.freeze({
          networkWarm: false,
          allowPrefetch: false,
          allowPrerender: false,
          accountLocalWarm: false,
          accountSecondaryIdle: false,
          headerImageLimit: 0,
          headerStyleAssetLimit: 0,
          headerSurfaceLimit: 0,
          footerImageLimit: 0,
          footerSurfaceLimit: 0,
          footerRootMargin: 0,
          prefetchBudget: 0,
          prerenderBudget: 0,
          activePrerenderLimit: 0,
          networkCooldownMs: 1000,
          retainedImageLimit: 0,
          stage01Budget: 0
        }),

      economy:
        Object.freeze({
          networkWarm: true,
          allowPrefetch: true,
          allowPrerender: false,
          accountLocalWarm: true,
          accountSecondaryIdle: false,
          headerImageLimit: 1,
          headerStyleAssetLimit: 2,
          headerSurfaceLimit: 2,
          footerImageLimit: 2,
          footerSurfaceLimit: 1,
          footerRootMargin: 650,
          prefetchBudget: 3,
          prerenderBudget: 0,
          activePrerenderLimit: 0,
          networkCooldownMs: 450,
          retainedImageLimit: 8,
          stage01Budget: 3
        }),

      standard:
        Object.freeze({
          networkWarm: true,
          allowPrefetch: true,
          allowPrerender: false,
          accountLocalWarm: true,
          accountSecondaryIdle: true,
          headerImageLimit: 2,
          headerStyleAssetLimit: 6,
          headerSurfaceLimit: 4,
          footerImageLimit: 6,
          footerSurfaceLimit: 2,
          footerRootMargin: 1000,
          prefetchBudget: 6,
          prerenderBudget: 0,
          activePrerenderLimit: 0,
          networkCooldownMs: 250,
          retainedImageLimit: 12,
          stage01Budget: 5
        }),

      performance:
        Object.freeze({
          networkWarm: true,
          allowPrefetch: true,
          allowPrerender: true,
          accountLocalWarm: true,
          accountSecondaryIdle: true,
          headerImageLimit: 2,
          headerStyleAssetLimit: 10,
          headerSurfaceLimit: 5,
          footerImageLimit: 10,
          footerSurfaceLimit: 2,
          footerRootMargin: 1400,
          prefetchBudget: 8,
          prerenderBudget: 2,
          activePrerenderLimit: 1,
          networkCooldownMs: 150,
          retainedImageLimit: 16,
          stage01Budget: 7
        })
    });

  function capabilitySignals() {
    const effectiveType =
      String(
        connection?.effectiveType ||
        ''
      ).toLowerCase();

    const memory =
      Number(
        navigator.deviceMemory || 0
      );

    const cores =
      Number(
        navigator.hardwareConcurrency || 0
      );

    const downlink =
      Number(
        connection?.downlink || 0
      );

    const rtt =
      Number(
        connection?.rtt || 0
      );

    return {
      saveData:
        Boolean(
          connection?.saveData
        ),
      effectiveType,
      memory,
      cores,
      downlink,
      rtt
    };
  }

  function computeTier() {
    const signals =
      capabilitySignals();

    let tier =
      'standard';

    let reason =
      'balanced-default';

    if (
      signals.saveData ||
      signals.effectiveType ===
        'slow-2g' ||
      signals.effectiveType ===
        '2g'
    ) {
      tier =
        'constrained';

      reason =
        signals.saveData
          ? 'save-data'
          : signals.effectiveType;
    }
    else if (
      signals.effectiveType ===
        '3g' ||
      (
        signals.memory > 0 &&
        signals.memory <= 2
      ) ||
      (
        signals.cores > 0 &&
        signals.cores <= 2
      )
    ) {
      tier =
        'economy';

      reason =
        signals.effectiveType === '3g'
          ? '3g'
          : (
              signals.memory > 0 &&
              signals.memory <= 2
            )
            ? 'low-memory'
            : 'low-cpu';
    }
    else {
      const strongNetwork =
        signals.effectiveType === '4g' ||
        (
          !signals.effectiveType &&
          signals.downlink >= 5
        );

      const strongDevice =
        (
          signals.memory >= 8
        ) ||
        (
          signals.memory >= 4 &&
          signals.cores >= 8
        ) ||
        (
          !signals.memory &&
          signals.cores >= 8
        );

      if (
        strongNetwork &&
        strongDevice
      ) {
        tier =
          'performance';

        reason =
          'strong-device-network';
      }
      else {
        tier =
          'standard';

        if (
          !signals.effectiveType &&
          !signals.memory &&
          !signals.cores
        ) {
          reason =
            'privacy-safe-default';
        }
        else {
          reason =
            'balanced-device-network';
        }
      }
    }

    state.tier =
      tier;

    state.tierReason =
      reason;

    state.capabilitySignals =
      signals;

    state.capabilityEvaluations++;

    return tier;
  }

  function tierPolicy() {
    const tier =
      computeTier();

    return (
      TIER_POLICIES[tier] ||
      TIER_POLICIES.standard
    );
  }

  function constrained() {
    return (
      computeTier() ===
      'constrained'
    );
  }

  function highConfidenceReason(
    reason = ''
  ) {
    return (
      reason.includes('touchstart') ||
      reason.includes('focusin') ||
      reason.includes('pointerdown') ||
      reason.includes('manual-high') ||
      reason.includes('account-intent') ||
      reason.includes('stage01-strong')
    );
  }

  function prerenderConfidenceReason(
    reason = ''
  ) {
    /*
      Prerender is intentionally stricter than normal high-confidence work.
      Mouse/pen pointerdown may prerender on Performance tier.
      Touch and keyboard focus remain prefetch-only.
    */
    return (
      reason.includes(
        'pointerdown-mouse'
      ) ||
      reason.includes(
        'pointerdown-pen'
      ) ||
      reason.includes(
        'manual-high'
      )
    );
  }

  function intentConfidence(
    reason = ''
  ) {
    if (
      prerenderConfidenceReason(
        reason
      )
    ) {
      return 'high';
    }

    if (
      reason.includes(
        'touchstart'
      ) ||
      reason.includes(
        'focusin'
      ) ||
      reason.includes(
        'dwell'
      ) ||
      reason.includes(
        'stage01-strong'
      )
    ) {
      return 'medium';
    }

    return 'low';
  }

  function pageIsPrerendering() {
    return (
      document.prerendering ===
      true
    );
  }

  function navigationEntry() {
    try {
      const entries =
        performance?.getEntriesByType?.(
          'navigation'
        ) || [];

      return entries[0] || null;
    }
    catch(_error) {
      return null;
    }
  }

  function refreshNavigationRuntimeMetrics(
    reason = 'metrics'
  ) {
    state.performanceReads++;

    const entry =
      navigationEntry();

    state.navigationMetricLastReason =
      reason;

    state.navigationMetricLastReadAt =
      Date.now();

    if (!entry) {
      state.navigationMetricsSupported =
        false;

      return null;
    }

    state.navigationMetricsSupported =
      true;

    state.navigationEntryType =
      String(
        entry.type || ''
      );

    const activationStart =
      Number(
        entry.activationStart || 0
      );

    const previouslyActivated =
      state.activatedFromPrerender;

    state.navigationActivationStart =
      Number.isFinite(
        activationStart
      )
        ? activationStart
        : 0;

    state.navigationDuration =
      Number(
        entry.duration || 0
      ) || 0;

    state.navigationResponseEnd =
      Number(
        entry.responseEnd || 0
      ) || 0;

    state.navigationDomContentLoadedEnd =
      Number(
        entry.domContentLoadedEventEnd ||
        0
      ) || 0;

    state.navigationLoadEventEnd =
      Number(
        entry.loadEventEnd || 0
      ) || 0;

    state.activatedFromPrerender =
      Boolean(
        state.navigationActivationStart >
        0
      );

    if (
      !previouslyActivated &&
      state.activatedFromPrerender
    ) {
      state.prerenderActivationsObserved++;
    }

    return entry;
  }

  function refreshLightReferences() {
    state.hotHeader =
      document.querySelector(
        '[data-nmr-header]'
      ) ||
      document.querySelector(
        'header'
      ) ||
      null;

    state.hotFooter =
      document.querySelector(
        '[data-nmr-footer]'
      ) ||
      document.querySelector(
        '.nmr-footer'
      ) ||
      document.querySelector(
        'footer'
      ) ||
      null;

    const snapshot =
      accountSnapshot();

    state.accountObservedRoot =
      snapshot.root || null;

    return snapshot;
  }

  function cancelPendingIntentTimers() {
    let cancelled = 0;

    for (
      const anchor of
      stage01IntentAnchors
    ) {
      if (
        stage01Cancel(
          anchor
        )
      ) {
        cancelled++;
      }
    }

    for (
      const owner of
      state.shellIntentOwners
    ) {
      if (
        cancelShellIntent(
          owner
        )
      ) {
        cancelled++;
      }
    }

    state.hiddenIntentCancellations +=
      cancelled;

    return cancelled;
  }

  function pauseLifecycleForHidden(
    reason = 'hidden'
  ) {
    if (
      state.lifecyclePaused
    ) {
      return false;
    }

    state.lifecyclePaused =
      true;

    state.lifecyclePauses++;

    state.lastPauseAt =
      Date.now();

    state.lastPauseReason =
      reason;

    cancelPendingIntentTimers();

    if (
      state.activeSpeculation
    ) {
      state.hiddenSpeculationStops++;

      removeSpeculation();
    }

    return true;
  }

  function lightResumeLifecycle(
    reason = 'visible',
    options = {}
  ) {
    const {
      force = false
    } = options;

    if (
      !state.pageLoaded ||
      pageIsPrerendering() ||
      document.visibilityState ===
        'hidden'
    ) {
      return false;
    }

    const now =
      Date.now();

    if (
      !force &&
      state.lastResumeAt &&
      now -
        state.lastResumeAt <
        state.visibilityCooldownMs
    ) {
      state.visibilityCooldownSkips++;

      return false;
    }

    const previousTier =
      state.tier;

    state.lifecyclePaused =
      false;

    state.lifecycleResumes++;

    state.lightRefreshes++;

    if (
      reason.includes(
        'bfcache'
      )
    ) {
      state.bfcacheLightRefreshes++;
    }

    state.lastResumeAt =
      now;

    state.lastResumeReason =
      reason;

    computeTier();

    refreshNavigationRuntimeMetrics(
      'resume-' + reason
    );

    const snapshot =
      refreshLightReferences();

    if (
      snapshot.root &&
      snapshot.modal &&
      (
        state.accountReady ||
        state.accountSecondaryArmed ||
        previousTier !== state.tier
      )
    ) {
      scheduleAccountIntegrityCheck(
        'resume-' + reason
      );
    }

    /*
      Important:
      No warmShell(), warmHeader(), warmFooter(), or speculative
      navigation is started merely because the document became visible.
    */
    return true;
  }

  function heavyWarmAllowed() {
    return Boolean(
      state.pageLoaded &&
      state.activated &&
      !pageIsPrerendering() &&
      document.visibilityState !==
        'hidden'
    );
  }

  function blockLifecycle(
    reason = 'blocked'
  ) {
    state.lifecycleBlocks++;

    state.skipped.push(
      'lifecycle:' + reason
    );

    if (
      state.skipped.length > 24
    ) {
      state.skipped.shift();
    }

    return false;
  }

  function bindInteractiveOnce() {
    if (
      state.interactiveBound
    ) {
      return true;
    }

    bindIntent();
    bindStage01Predictor();

    state.interactiveBound =
      true;

    return true;
  }

  function startHeavyWarmLifecycle(
    reason = 'lifecycle'
  ) {
    if (
      !heavyWarmAllowed()
    ) {
      return false;
    }

    if (
      state.heavyWarmStarted
    ) {
      return true;
    }

    state.heavyWarmStarted =
      true;

    state.heavyWarmAt =
      Date.now();

    state.heavyWarmReason =
      reason;

    watchAccountRoot();
    installFooterObserver();
    initialIdleWarm();

    return true;
  }

  function markPageLoaded(
    reason = 'window-load'
  ) {
    if (!state.pageLoaded) {
      state.pageLoaded =
        true;

      state.loadAt =
        Date.now();
    }

    return startHeavyWarmLifecycle(
      reason
    );
  }

  function markActivated(
    reason = 'prerender-activation'
  ) {
    state.prerendering =
      false;

    state.activated =
      true;

    if (!state.activationAt) {
      state.activationAt =
        Date.now();
    }

    refreshNavigationRuntimeMetrics(
      reason
    );

    window.setTimeout(
      () => {
        refreshNavigationRuntimeMetrics(
          reason + '-settled'
        );
      },
      0
    );

    return startHeavyWarmLifecycle(
      reason
    );
  }

  function idle(fn, timeout = 1400) {
    if ('requestIdleCallback' in window) {
      return window.requestIdleCallback(
        fn,
        { timeout }
      );
    }

    return window.setTimeout(
      () => fn({
        didTimeout: true,
        timeRemaining: () => 0
      }),
      Math.min(timeout, 700)
    );
  }

  function normalizeUrl(raw) {
    try {
      const url =
        new URL(
          raw,
          window.location.href
        );

      if (url.origin !== location.origin) {
        return null;
      }

      if (
        url.protocol !== 'http:' &&
        url.protocol !== 'https:'
      ) {
        return null;
      }

      url.hash = '';

      return url;
    }
    catch(_error) {
      return null;
    }
  }

  function normalizeAssetUrl(raw) {
    try {
      const url =
        new URL(
          raw,
          window.location.href
        );

      if (
        url.protocol !== 'http:' &&
        url.protocol !== 'https:'
      ) {
        return null;
      }

      return url;
    }
    catch(_error) {
      return null;
    }
  }

  function routePrefixMatch(
    path,
    prefix
  ) {
    return (
      path === prefix ||
      path.startsWith(
        prefix + '/'
      )
    );
  }

  function normalizeRoutePath(
    pathname = '/'
  ) {
    let path =
      String(pathname || '/');

    try {
      path =
        decodeURIComponent(
          path
        );
    }
    catch(_error) {}

    path =
      path
        .replace(/\\/g, '/')
        .replace(/\/+/g, '/')
        .toLowerCase();

    if (!path.startsWith('/')) {
      path =
        '/' + path;
    }

    if (
      path.length > 1 &&
      path.endsWith('/')
    ) {
      path =
        path.slice(0, -1);
    }

    return path;
  }

  function stripLocalePrefix(
    pathname = '/'
  ) {
    const normalized =
      normalizeRoutePath(
        pathname
      );

    if (normalized === '/') {
      return {
        path: '/',
        locale: ''
      };
    }

    const segments =
      normalized
        .split('/')
        .filter(Boolean);

    const first =
      segments[0] || '';

    const localeLike =
      /^[a-z]{2}(?:-[a-z]{2})?$/
        .test(first);

    if (!localeLike) {
      return {
        path: normalized,
        locale: ''
      };
    }

    const remainder =
      segments
        .slice(1)
        .join('/');

    return {
      path:
        remainder
          ? '/' + remainder
          : '/',
      locale: first
    };
  }

  function routeFamily(
    path = '/'
  ) {
    if (path === '/') {
      return 'root';
    }

    return (
      path
        .split('/')
        .filter(Boolean)[0] ||
      'unknown'
    );
  }

  function blockedRoutePrefix(
    path
  ) {
    return (
      ROUTE_BLOCKED_PREFIXES
        .find(prefix =>
          routePrefixMatch(
            path,
            prefix
          )
        ) ||
      ''
    );
  }

  function prefetchOnlyRoutePrefix(
    path
  ) {
    return (
      ROUTE_PREFETCH_ONLY_PREFIXES
        .find(prefix =>
          routePrefixMatch(
            path,
            prefix
          )
        ) ||
      ''
    );
  }

  function prerenderSafeRoutePrefix(
    path
  ) {
    return (
      ROUTE_PRERENDER_SAFE_PREFIXES
        .find(prefix =>
          routePrefixMatch(
            path,
            prefix
          )
        ) ||
      ''
    );
  }

  function hasMutationQuery(
    url
  ) {
    return ROUTE_MUTATION_QUERY_KEYS
      .some(key =>
        url.searchParams.has(
          key
        )
      );
  }

  function nonDocumentRoute(
    path
  ) {
    return ROUTE_NON_DOCUMENT_SUFFIXES
      .some(suffix =>
        path.endsWith(
          suffix
        )
      );
  }

  function recordRouteDecision(
    decision,
    source = 'unknown'
  ) {
    state.routeChecks++;

    if (decision.locale) {
      state.routeLocalizedChecks++;
    }

    if (
      decision.mode ===
        'blocked'
    ) {
      state.routeBlocked++;

      if (
        decision.reason ===
          'not-allowlisted'
      ) {
        state.routeUnknownBlocked++;
      }

      const sample =
        [
          source,
          decision.path,
          decision.reason
        ].join(':');

      if (
        !state.routeBlockedSamples
          .includes(sample)
      ) {
        state.routeBlockedSamples.push(
          sample
        );

        if (
          state.routeBlockedSamples.length >
          12
        ) {
          state.routeBlockedSamples.shift();
        }
      }
    }
    else if (
      decision.mode ===
        'prefetch-only'
    ) {
      state.routePrefetchOnly++;
    }
    else if (
      decision.mode ===
        'prerender-safe'
    ) {
      state.routePrerenderSafe++;
    }

    state.lastRouteDecision = {
      source,
      path: decision.path,
      locale: decision.locale,
      family: decision.family,
      mode: decision.mode,
      reason: decision.reason
    };
  }

  function classifyRoute(
    raw,
    options = {}
  ) {
    const {
      record = true,
      source = 'unknown'
    } = options;

    const url =
      normalizeUrl(raw);

    if (!url) {
      const invalid = {
        url: null,
        path: '',
        locale: '',
        family: 'invalid',
        mode: 'blocked',
        reason: 'invalid-or-external',
        allowPrefetch: false,
        allowPrerender: false
      };

      if (record) {
        recordRouteDecision(
          invalid,
          source
        );
      }

      return invalid;
    }

    const localized =
      stripLocalePrefix(
        url.pathname
      );

    const path =
      localized.path;

    const family =
      routeFamily(
        path
      );

    let mode =
      'blocked';

    let reason =
      'not-allowlisted';

    if (
      hasMutationQuery(
        url
      )
    ) {
      reason =
        'mutation-query';
    }
    else if (
      nonDocumentRoute(
        path
      )
    ) {
      reason =
        'non-document';
    }
    else {
      const blockedPrefix =
        blockedRoutePrefix(
          path
        );

      if (blockedPrefix) {
        reason =
          'blocked:' +
          blockedPrefix;
      }
      else if (path === '/') {
        mode =
          'prerender-safe';

        reason =
          'safe-root';
      }
      else {
        const prefetchPrefix =
          prefetchOnlyRoutePrefix(
            path
          );

        if (prefetchPrefix) {
          mode =
            'prefetch-only';

          reason =
            'prefetch-only:' +
            prefetchPrefix;
        }
        else {
          const safePrefix =
            prerenderSafeRoutePrefix(
              path
            );

          if (safePrefix) {
            mode =
              'prerender-safe';

            reason =
              'safe:' +
              safePrefix;
          }
        }
      }
    }

    const decision = {
      url,
      path,
      locale:
        localized.locale,
      family,
      mode,
      reason,
      allowPrefetch:
        mode ===
          'prefetch-only' ||
        mode ===
          'prerender-safe',
      allowPrerender:
        mode ===
          'prerender-safe'
    };

    if (record) {
      recordRouteDecision(
        decision,
        source
      );
    }

    return decision;
  }

  function sameDocument(url) {
    return (
      url.pathname === location.pathname &&
      url.search === location.search
    );
  }

  function currentBudgetPolicy() {
    /*
      Budget reads must be side-effect free.
      Tier re-evaluation is owned by normal capability/lifecycle paths.
    */
    const policy =
      TIER_POLICIES[state.tier] ||
      TIER_POLICIES.standard;

    return {
      prefetchBudget:
        Math.max(
          0,
          Number(
            policy.prefetchBudget || 0
          )
        ),
      prerenderBudget:
        Math.max(
          0,
          Number(
            policy.prerenderBudget || 0
          )
        ),
      activePrerenderLimit:
        Math.max(
          0,
          Number(
            policy.activePrerenderLimit || 0
          )
        ),
      networkCooldownMs:
        Math.max(
          0,
          Number(
            policy.networkCooldownMs || 0
          )
        ),
      retainedImageLimit:
        Math.max(
          0,
          Number(
            policy.retainedImageLimit || 0
          )
        )
    };
  }

  function networkCooldownAllows(
    href,
    highConfidence = false
  ) {
    const budget =
      currentBudgetPolicy();

    if (
      highConfidence ||
      !state.lastNetworkWarmAt ||
      state.lastNetworkWarmHref ===
        href
    ) {
      return true;
    }

    const elapsed =
      Date.now() -
      state.lastNetworkWarmAt;

    if (
      elapsed <
      budget.networkCooldownMs
    ) {
      state.cooldownRejected++;

      return false;
    }

    return true;
  }

  function markNetworkWarm(
    href
  ) {
    state.lastNetworkWarmAt =
      Date.now();

    state.lastNetworkWarmHref =
      href;
  }

  function reservePrefetchBudget(
    href,
    options = {}
  ) {
    const {
      highConfidence = false
    } = options;

    if (
      state.prefetchBudgetUrls.has(
        href
      )
    ) {
      return 'duplicate';
    }

    const budget =
      currentBudgetPolicy();

    if (
      budget.prefetchBudget <= 0 ||
      state.prefetchRequests >=
        budget.prefetchBudget
    ) {
      state.prefetchBudgetRejected++;

      return 'blocked';
    }

    if (
      !networkCooldownAllows(
        href,
        highConfidence
      )
    ) {
      return 'blocked';
    }

    state.prefetchBudgetUrls.add(
      href
    );

    state.prefetchRequests++;

    markNetworkWarm(
      href
    );

    return 'reserved';
  }

  function reservePrerenderBudget(
    href,
    options = {}
  ) {
    const {
      highConfidence = true
    } = options;

    const budget =
      currentBudgetPolicy();

    if (
      budget.prerenderBudget <= 0 ||
      budget.activePrerenderLimit <= 0
    ) {
      state.prerenderBudgetRejected++;

      return 'blocked';
    }

    if (
      state.activePrerenderUrl ===
        href &&
      state.activeSpeculation?.isConnected
    ) {
      return 'active';
    }

    if (
      state.prerenderRequests >=
        budget.prerenderBudget
    ) {
      state.prerenderBudgetRejected++;

      return 'blocked';
    }

    if (
      !networkCooldownAllows(
        href,
        highConfidence
      )
    ) {
      return 'blocked';
    }

    state.prerenderRequests++;

    markNetworkWarm(
      href
    );

    return 'reserved';
  }

  function retainImageObject(
    image
  ) {
    if (!image) {
      return false;
    }

    const budget =
      currentBudgetPolicy();

    const limit =
      budget.retainedImageLimit;

    if (limit <= 0) {
      return false;
    }

    if (
      state.retainedImages.includes(
        image
      )
    ) {
      return true;
    }

    while (
      state.retainedImages.length >=
        limit
    ) {
      state.retainedImages.shift();
    }

    state.retainedImages.push(
      image
    );

    return true;
  }

  function removeSpeculation() {
    if (
      state.activeSpeculation?.isConnected
    ) {
      state.activeSpeculation.remove();
    }

    state.activeSpeculation = null;
    state.activePrerenderUrl = '';
  }

  function recordNavigationPolicy(
    decision
  ) {
    state.policyDecisions++;

    if (
      decision.mode ===
        'prerender'
    ) {
      state.policyPrerenderDecisions++;
    }
    else if (
      decision.mode ===
        'prefetch'
    ) {
      state.policyPrefetchDecisions++;
    }
    else {
      state.policyNoWarmDecisions++;
    }

    const family =
      decision.family ||
      'unknown';

    state.policyFamilyCounts[family] =
      (
        state.policyFamilyCounts[family] ||
        0
      ) + 1;

    state.lastNavigationPolicy = {
      tier: decision.tier,
      family,
      confidence:
        decision.confidence,
      mode:
        decision.mode,
      eagerness:
        decision.eagerness,
      reason:
        decision.reason,
      routeMode:
        decision.routeMode
    };

    return decision;
  }

  function navigationPolicyDecision(
    route,
    tier,
    confidence,
    reason = ''
  ) {
    const family =
      route?.family ||
      'unknown';

    const routeMode =
      route?.mode ||
      'blocked';

    const decision = {
      tier,
      family,
      confidence,
      mode: 'none',
      eagerness: '',
      reason:
        'policy-no-network',
      routeMode
    };

    if (
      !route?.allowPrefetch
    ) {
      decision.reason =
        'route-blocked';

      return recordNavigationPolicy(
        decision
      );
    }

    if (
      tier === 'constrained'
    ) {
      decision.reason =
        'constrained-tier';

      return recordNavigationPolicy(
        decision
      );
    }

    if (
      routeMode ===
        'prefetch-only'
    ) {
      decision.mode =
        'prefetch';

      decision.reason =
        'route-prefetch-only';

      return recordNavigationPolicy(
        decision
      );
    }

    if (
      tier === 'economy'
    ) {
      if (
        highConfidenceReason(
          reason
        )
      ) {
        decision.mode =
          'prefetch';

        decision.reason =
          'economy-strong-intent';
      }
      else {
        decision.reason =
          'economy-weak-intent';
      }

      return recordNavigationPolicy(
        decision
      );
    }

    if (
      tier === 'standard'
    ) {
      decision.mode =
        'prefetch';

      decision.reason =
        'standard-prefetch';

      return recordNavigationPolicy(
        decision
      );
    }

    /*
      Performance tier:
      - Products/collections are the highest-value commerce targets.
      - Home can prerender only on strong explicit intent.
      - Editorial/legal content stays prefetch-only.
      - Touch/focus/dwell never request prerender.
    */
    const commerceFamily =
      family === 'products' ||
      family === 'collections';

    const rootFamily =
      family === 'root';

    const contentFamily =
      family === 'pages' ||
      family === 'blogs' ||
      family === 'policies';

    const strongPrerenderIntent =
      confidence === 'high' &&
      prerenderConfidenceReason(
        reason
      );

    if (
      strongPrerenderIntent &&
      route.allowPrerender &&
      (
        commerceFamily ||
        rootFamily
      )
    ) {
      decision.mode =
        'prerender';

      decision.eagerness =
        'moderate';

      decision.reason =
        commerceFamily
          ? 'performance-commerce-high'
          : 'performance-root-high';

      return recordNavigationPolicy(
        decision
      );
    }

    decision.mode =
      'prefetch';

    if (contentFamily) {
      decision.reason =
        'performance-content-prefetch';
    }
    else if (
      confidence === 'medium'
    ) {
      decision.reason =
        'performance-medium-prefetch';
    }
    else {
      decision.reason =
        'performance-safe-prefetch';
    }

    return recordNavigationPolicy(
      decision
    );
  }

  function installSpeculation(
    url,
    requestedMode,
    options = {}
  ) {
    const {
      highConfidence = false,
      source = 'unknown',
      eagerness = 'moderate'
    } = options;

    const policy =
      tierPolicy();

    if (
      designMode() ||
      !heavyWarmAllowed() ||
      !policy.networkWarm ||
      document.visibilityState !==
        'visible'
    ) {
      return false;
    }

    const route =
      classifyRoute(
        url,
        {
          record: false,
          source: 'install'
        }
      );

    if (
      !route.allowPrefetch
    ) {
      return false;
    }

    const href =
      route.url.href;

    let mode =
      requestedMode;

    if (
      mode === 'prerender' &&
      !route.allowPrerender
    ) {
      state.policyDowngrades++;

      mode =
        'prefetch';
    }

    if (
      mode === 'prerender' &&
      !policy.allowPrerender
    ) {
      state.policyDowngrades++;

      mode =
        policy.allowPrefetch
          ? 'prefetch'
          : 'none';
    }

    if (
      mode === 'prefetch' &&
      !policy.allowPrefetch
    ) {
      mode =
        'none';
    }

    if (mode === 'none') {
      return false;
    }

    const supportsSpeculationRules =
      typeof HTMLScriptElement.supports ===
        'function' &&
      HTMLScriptElement.supports(
        'speculationrules'
      );

    if (
      mode === 'prerender' &&
      !supportsSpeculationRules
    ) {
      state.policyUnsupportedFallbacks++;
      state.policyDowngrades++;

      mode =
        'prefetch';
    }

    if (
      mode === 'prerender'
    ) {
      const reservation =
        reservePrerenderBudget(
          href,
          {
            highConfidence,
            source
          }
        );

      if (
        reservation ===
          'active'
      ) {
        return true;
      }

      if (
        reservation ===
          'reserved'
      ) {
        if (
          state.activeSpeculation
        ) {
          state.prerenderReplacements++;
        }

        removeSpeculation();

        const node =
          document.createElement(
            'script'
          );

        node.type =
          'speculationrules';

        node.textContent =
          JSON.stringify({
            prerender: [
              {
                urls: [href],
                eagerness:
                  eagerness ||
                  'moderate'
              }
            ]
          });

        document.head.appendChild(
          node
        );

        state.prerenderRulesInserted++;

        state.activeSpeculation =
          node;

        state.activePrerenderUrl =
          href;

        state.prerendered.add(
          href
        );

        return true;
      }

      /*
        Prerender budget exhausted:
        preserve user benefit by degrading to prefetch.
      */
      state.policyDowngrades++;

      mode =
        'prefetch';
    }

    if (
      !policy.allowPrefetch ||
      mode !== 'prefetch'
    ) {
      return false;
    }

    if (
      state.prefetched.has(href)
    ) {
      return true;
    }

    const reservation =
      reservePrefetchBudget(
        href,
        {
          highConfidence,
          source
        }
      );

    if (
      reservation ===
        'duplicate'
    ) {
      return true;
    }

    if (
      reservation !==
        'reserved'
    ) {
      return false;
    }

    const link =
      document.createElement(
        'link'
      );

    link.rel =
      'prefetch';

    link.href =
      href;

    link.as =
      'document';

    try {
      link.fetchPriority =
        highConfidence
          ? 'auto'
          : 'low';
    }
    catch(_error) {}

    document.head.appendChild(
      link
    );

    state.prefetchLinksInserted++;

    state.prefetched.add(
      href
    );

    return true;
  }

  function warmUrl(
    raw,
    reason = 'manual'
  ) {
    if (
      !heavyWarmAllowed()
    ) {
      return blockLifecycle(
        'warm-url-before-ready'
      );
    }

    const route =
      classifyRoute(
        raw,
        {
          source: 'warm-url'
        }
      );

    const url =
      route.url;

    if (
      !route.allowPrefetch ||
      !url ||
      sameDocument(url)
    ) {
      return false;
    }

    const tier =
      computeTier();

    const policy =
      TIER_POLICIES[tier] ||
      TIER_POLICIES.standard;

    if (
      !policy.networkWarm
    ) {
      return false;
    }

    const confidence =
      intentConfidence(
        reason
      );

    const navDecision =
      navigationPolicyDecision(
        route,
        tier,
        confidence,
        reason
      );

    if (
      navDecision.mode ===
        'none'
    ) {
      return false;
    }

    state.lastIntent =
      reason +
      ':' +
      route.path;

    state.lastIntentAt =
      Date.now();

    state.lastIntentConfidence =
      confidence;

    state.intentExecuted++;

    if (confidence === 'high') {
      state.intentHighConfidence++;
    }
    else if (
      confidence === 'medium'
    ) {
      state.intentMediumConfidence++;
    }

    return installSpeculation(
      url,
      navDecision.mode,
      {
        highConfidence:
          confidence === 'high',
        source:
          reason,
        eagerness:
          navDecision.eagerness ||
          'moderate'
      }
    );
  }

  function touchStyles(root) {
    if (!root) return 0;

    const selectors = [
      '.otp-account-modal',
      '.otp-card',
      '.otp-btn--google',
      '.otp-btn--apple',
      '.otp-field',
      '.otp-btn--primary',
      '.otp-account-modal__close',
      '.nmr-auth-v6__card',
      '.nmr-auth-v6__back',
      '.country-dropdown-panel'
    ];

    let count = 0;

    for (const selector of selectors) {
      const node =
        root.querySelector?.(selector);

      if (!node) continue;

      try {
        getComputedStyle(node).opacity;
        count++;
      }
      catch(_error) {}
    }

    return count;
  }

  function accountSnapshot() {
    const root =
      document.getElementById(
        'otp-nca-account-modal'
      );

    const modal =
      root?.querySelector(
        '.otp-account-modal'
      ) || null;

    const host =
      modal?.querySelector(
        ':scope > .nmr-auth-v6-secondary-host'
      ) ||
      modal?.querySelector(
        '.nmr-auth-v6-secondary-host'
      ) ||
      null;

    const secondaryShell =
      host?.querySelector(
        '[data-nmr-v6-secondary]'
      ) || null;

    const form =
      host?.querySelector(
        '[data-nmr-v6-form]'
      ) || null;

    const back =
      host?.querySelector(
        '[data-nmr-v6-back]'
      ) || null;

    const hostWarmed =
      Boolean(
        host?.isConnected &&
        host?.dataset?.nmrWarmed ===
          'true'
      );

    const rootWarmed =
      Boolean(
        root?.getAttribute(
          'data-nmr-account-warmed'
        ) === 'true'
      );

    const secondaryReady =
      Boolean(
        root &&
        modal &&
        hostWarmed &&
        rootWarmed &&
        secondaryShell &&
        form &&
        back
      );

    const secondaryOpen =
      Boolean(
        modal?.classList.contains(
          'nmr-auth-v6-secondary-open'
        ) ||
        (
          host &&
          host.hidden === false
        )
      );

    return {
      root,
      modal,
      host,
      secondaryShell,
      form,
      back,
      hostWarmed,
      rootWarmed,
      secondaryReady,
      secondaryOpen,
      signature:
        [
          Boolean(root),
          Boolean(modal),
          Boolean(host),
          hostWarmed,
          rootWarmed,
          secondaryReady,
          secondaryOpen
        ].map(Boolean).join(':')
    };
  }

  function requestSecondaryWarm(
    reason = 'account-warm',
    force = false
  ) {
    const snapshot =
      accountSnapshot();

    if (
      snapshot.secondaryReady
    ) {
      state.secondaryRequested =
        true;

      state.accountSecondaryArmed =
        true;

      return true;
    }

    const now =
      Date.now();

    if (
      !force &&
      now -
        state.accountLastRequestAt <
        250
    ) {
      return false;
    }

    state.accountLastRequestAt =
      now;

    state.accountWarmRequests++;

    document.dispatchEvent(
      new CustomEvent(
        'nmr:warmup:account-secondary',
        {
          detail: {
            reason,
            requestedAt: now,
            version: 'v2'
          }
        }
      )
    );

    const after =
      accountSnapshot();

    state.secondaryRequested =
      Boolean(
        after.host
      );

    if (
      after.secondaryReady
    ) {
      state.accountSecondaryArmed =
        true;

      state.accountLastSuccessAt =
        Date.now();

      return true;
    }

    state.accountWarmFailures++;

    return false;
  }

  function canAccountRewarm() {
    const now =
      Date.now();

    if (
      !state.accountRewarmWindowAt ||
      now -
        state.accountRewarmWindowAt >
        30000
    ) {
      state.accountRewarmWindowAt =
        now;

      state.accountRewarmsInWindow =
        0;
    }

    if (
      state.accountRewarmsInWindow >=
        4
    ) {
      return false;
    }

    state.accountRewarmsInWindow++;

    return true;
  }

  function warmAccount(
    reason = 'idle'
  ) {
    if (
      !heavyWarmAllowed()
    ) {
      return blockLifecycle(
        'account-before-ready'
      );
    }

    state.accountRuns++;
    state.accountLastReason = reason;

    let snapshot =
      accountSnapshot();

    state.accountLastSignature =
      snapshot.signature;

    if (
      !snapshot.root ||
      !snapshot.modal
    ) {
      state.accountReady =
        false;

      return false;
    }

    const tier =
      computeTier();

    const policy =
      TIER_POLICIES[tier] ||
      TIER_POLICIES.standard;

    const intent =
      highConfidenceReason(
        reason
      );

    if (
      tier === 'constrained' &&
      !intent
    ) {
      try {
        snapshot.root.dataset.nmrWarmTier =
          tier;
      }
      catch(_error) {}

      state.accountReady =
        false;

      return true;
    }

    if (
      policy.accountLocalWarm ||
      intent
    ) {
      touchStyles(
        snapshot.root
      );
    }

    const needSecondary =
      Boolean(
        policy.accountSecondaryIdle ||
        intent ||
        state.accountSecondaryArmed ||
        snapshot.secondaryOpen
      );

    if (
      needSecondary &&
      !snapshot.secondaryReady
    ) {
      const isRewarm =
        state.accountWarmSuccesses > 0 ||
        state.accountSecondaryArmed;

      if (
        !isRewarm ||
        canAccountRewarm()
      ) {
        if (isRewarm) {
          state.accountRewarms++;
        }

        requestSecondaryWarm(
          reason,
          intent
        );

        snapshot =
          accountSnapshot();
      }
    }

    const localReady =
      Boolean(
        policy.accountLocalWarm ||
        intent
      );

    const ready =
      Boolean(
        localReady &&
        (
          !needSecondary ||
          snapshot.secondaryReady
        )
      );

    try {
      snapshot.root.dataset.nmrWarmReason =
        reason;

      snapshot.root.dataset.nmrWarmTier =
        tier;

      snapshot.root.dataset.nmrWarmState =
        ready
          ? 'ready'
          : needSecondary
            ? 'partial'
            : 'local';
    }
    catch(_error) {}

    state.accountReady =
      ready;

    state.secondaryRequested =
      Boolean(
        snapshot.host
      );

    state.accountLastSignature =
      snapshot.signature;

    if (ready) {
      state.accountWarmSuccesses++;
      state.accountLastSuccessAt =
        Date.now();

      if (
        snapshot.secondaryReady
      ) {
        state.accountSecondaryArmed =
          true;
      }
    }
    else if (
      needSecondary
    ) {
      state.accountWarmFailures++;
    }

    return true;
  }

  function mutationTouchesAccount(
    record
  ) {
    const target =
      record.target instanceof Element
        ? record.target
        : record.target?.parentElement;

    if (
      target?.id ===
        'otp-nca-account-modal' ||
      target?.closest?.(
        '#otp-nca-account-modal'
      )
    ) {
      return true;
    }

    const nodes = [
      ...record.addedNodes,
      ...record.removedNodes
    ];

    return nodes.some(node => {
      if (!(node instanceof Element)) {
        return false;
      }

      return Boolean(
        node.id ===
          'otp-nca-account-modal' ||
        node.matches?.(
          '.nmr-auth-v6-secondary-host'
        ) ||
        node.querySelector?.(
          '#otp-nca-account-modal, .nmr-auth-v6-secondary-host'
        )
      );
    });
  }

  function scheduleAccountIntegrityCheck(
    reason = 'mutation'
  ) {
    window.clearTimeout(
      state.accountIntegrityTimer
    );

    state.accountIntegrityTimer =
      window.setTimeout(
        () => {
          state.accountIntegrityTimer =
            0;

          if (
            !heavyWarmAllowed()
          ) {
            return;
          }

          const snapshot =
            accountSnapshot();

          state.accountLastSignature =
            snapshot.signature;

          if (
            !snapshot.root ||
            !snapshot.modal
          ) {
            state.accountReady =
              false;

            return;
          }

          const tier =
            computeTier();

          const policy =
            TIER_POLICIES[tier] ||
            TIER_POLICIES.standard;

          const shouldKeepSecondary =
            Boolean(
              policy.accountSecondaryIdle ||
              state.accountSecondaryArmed ||
              snapshot.secondaryOpen
            );

          if (
            shouldKeepSecondary &&
            !snapshot.secondaryReady
          ) {
            warmAccount(
              'integrity-' + reason
            );

            return;
          }

          if (
            snapshot.secondaryReady
          ) {
            state.accountSecondaryArmed =
              true;

            state.accountReady =
              tier !== 'constrained';
          }
        },
        90
      );
  }

  function installAccountIntegrityObserver() {
    if (state.observer) {
      return true;
    }

    state.observer =
      new MutationObserver(
        records => {
          if (
            records.some(
              mutationTouchesAccount
            )
          ) {
            scheduleAccountIntegrityCheck(
              'remount'
            );
          }
        }
      );

    state.observer.observe(
      document.documentElement,
      {
        childList: true,
        subtree: true
      }
    );

    return true;
  }

  function watchAccountRoot() {
    installAccountIntegrityObserver();

    const snapshot =
      accountSnapshot();

    state.accountObservedRoot =
      snapshot.root || null;

    if (
      snapshot.root &&
      snapshot.modal
    ) {
      warmAccount(
        'initial-root'
      );

      return true;
    }

    scheduleAccountIntegrityCheck(
      'root-await'
    );

    return false;
  }

  document.addEventListener(
    'nmr:warmup:account-secondary:ready',
    () => {
      if (
        !heavyWarmAllowed()
      ) {
        return;
      }

      const snapshot =
        accountSnapshot();

      state.accountLastSignature =
        snapshot.signature;

      if (
        snapshot.secondaryReady
      ) {
        state.secondaryRequested =
          true;

        state.accountSecondaryArmed =
          true;

        state.accountReady =
          true;

        state.accountLastSuccessAt =
          Date.now();
      }
    }
  );

  function imageUrl(img) {
    return (
      img.currentSrc ||
      img.src ||
      img.getAttribute?.('src') ||
      ''
    );
  }

  function retainImage(raw) {
    const url =
      normalizeAssetUrl(raw);

    if (!url) {
      return false;
    }

    const src =
      url.href;

    if (
      state.retainedImages.some(
        image =>
          image.src === src
      )
    ) {
      return true;
    }

    const image =
      new Image();

    image.decoding =
      'async';

    if (
      !retainImageObject(
        image
      )
    ) {
      return false;
    }

    image.src =
      src;

    image.decode?.()
      .then(() => {
        state.decodedImages++;
      })
      .catch(() => {});

    return true;
  }

  function warmImages(
    scope,
    limit = 0
  ) {
    const policy =
      tierPolicy();

    if (
      !scope ||
      !policy.networkWarm ||
      limit <= 0
    ) {
      return 0;
    }

    let count = 0;

    const images =
      scope.querySelectorAll?.(
        'img'
      ) || [];

    for (const img of images) {
      if (count >= limit) {
        break;
      }

      const src =
        imageUrl(img);

      if (!src) {
        continue;
      }

      if (retainImage(src)) {
        count++;
      }
    }

    return count;
  }

  function effectiveHeaderMode() {
    const declared =
      document.documentElement
        .dataset.nmrColorMode ||
      'light';

    if (declared === 'system') {
      return window.matchMedia?.(
        '(prefers-color-scheme: dark)'
      )?.matches
        ? 'dark'
        : 'light';
    }

    return declared === 'dark'
      ? 'dark'
      : 'light';
  }

  function decodeExistingImage(image) {
    if (!image) {
      return false;
    }

    try {
      image.decode?.()
        .then(() => {
          state.headerDecodedImages++;
        })
        .catch(() => {});
    }
    catch(_error) {}

    return true;
  }

  function cssUrls(value) {
    if (
      !value ||
      value === 'none'
    ) {
      return [];
    }

    return (
      value.match(/url\([^)]*\)/g) || []
    )
    .map(token =>
      token
        .slice(4, -1)
        .trim()
        .replace(/^["']|["']$/g, '')
    )
    .filter(Boolean);
  }

  function warmHeaderStyleAssets(limit = 0) {
    if (
      limit <= 0 ||
      !heavyWarmAllowed()
    ) {
      return 0;
    }

    const tools =
      document.querySelectorAll(
        '[data-nmr-header] .nmr-header__mode-tool-icon'
      );

    let count = 0;

    for (const tool of tools) {
      if (count >= limit) {
        break;
      }

      let background = '';

      try {
        background =
          getComputedStyle(tool)
            .backgroundImage;
      }
      catch(_error) {}

      for (
        const raw of cssUrls(background)
      ) {
        if (count >= limit) {
          break;
        }

        const url =
          normalizeAssetUrl(raw);

        if (
          !url ||
          state.headerStyleAssets.has(
            url.href
          )
        ) {
          continue;
        }

        const image =
          new Image();

        image.decoding =
          'async';

        image.src =
          url.href;

        if (
          !retainImageObject(
            image
          )
        ) {
          continue;
        }

        state.headerStyleAssets.add(
          url.href
        );

        image.decode?.()
          .then(() => {
            state.decodedImages++;
            state.headerDecodedImages++;
          })
          .catch(() => {});

        count++;
      }
    }

    return count;
  }

  function touchHeaderSurfaces(limit = 0) {
    if (
      limit <= 0 ||
      !heavyWarmAllowed()
    ) {
      return 0;
    }

    const selectors = [
      '[data-nmr-header]',
      '[data-nmr-layer="search"] .nmr-search-card',
      '[data-nmr-layer="display"] .nmr-display-settings__panel',
      '[data-nmr-layer="localization"] .nmr-localization-card',
      '[data-nmr-menu-drawer] .nmr-menu-drawer__panel'
    ];

    let count = 0;

    for (
      const selector of selectors
    ) {
      if (count >= limit) {
        break;
      }

      const node =
        document.querySelector(
          selector
        );

      if (!node) {
        continue;
      }

      try {
        const style =
          getComputedStyle(node);

        style.backgroundColor;
        style.color;
        style.borderRadius;
        style.backdropFilter;
        style.webkitBackdropFilter;

        count++;
      }
      catch(_error) {}
    }

    state.headerSurfacesTouched +=
      count;

    return count;
  }

  function bindHeaderModeWarm() {
    if (
      state.headerModeObserver ||
      !document.documentElement
    ) {
      return;
    }

    state.headerModeObserver =
      new MutationObserver(
        mutations => {
          const changed =
            mutations.some(
              mutation =>
                mutation.type ===
                  'attributes' &&
                mutation.attributeName ===
                  'data-nmr-color-mode'
            );

          if (
            changed &&
            heavyWarmAllowed()
          ) {
            warmHeader(
              'color-mode-change'
            );
          }
        }
      );

    state.headerModeObserver.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          'data-nmr-color-mode'
        ]
      }
    );
  }

  function warmHeader(reason = 'idle') {
    if (
      !heavyWarmAllowed()
    ) {
      return blockLifecycle(
        'header-before-ready'
      );
    }

    const tier =
      computeTier();

    const policy =
      TIER_POLICIES[tier] ||
      TIER_POLICIES.standard;

    const header =
      document.querySelector(
        '[data-nmr-header]'
      );

    if (!header) {
      return false;
    }

    const mode =
      effectiveHeaderMode();

    if (
      state.headerWarmReady &&
      state.headerLastMode === mode &&
      state.headerLastTier === tier &&
      reason === 'header-intent'
    ) {
      return true;
    }

    state.headerWarmRuns++;
    state.hotHeader = header;

    const activeLogo =
      document.querySelector(
        '.nmr-header__logo.nmr-mode-logo--' +
        mode
      );

    if (activeLogo) {
      decodeExistingImage(
        activeLogo
      );
    }

    if (
      policy.headerImageLimit > 1
    ) {
      const inactiveMode =
        mode === 'dark'
          ? 'light'
          : 'dark';

      const inactiveLogo =
        document.querySelector(
          '.nmr-header__logo.nmr-mode-logo--' +
          inactiveMode
        );

      if (
        inactiveLogo &&
        inactiveLogo !== activeLogo
      ) {
        decodeExistingImage(
          inactiveLogo
        );
      }
    }

    warmHeaderStyleAssets(
      policy.headerStyleAssetLimit
    );

    touchHeaderSurfaces(
      policy.headerSurfaceLimit
    );

    bindHeaderModeWarm();

    try {
      header.dataset.nmrHeaderWarm =
        'true';

      header.dataset.nmrHeaderWarmTier =
        tier;

      header.dataset.nmrHeaderWarmMode =
        mode;
    }
    catch(_error) {}

    state.headerWarmReady = true;
    state.headerWarmAt = Date.now();
    state.headerWarmReason = reason;
    state.headerLastMode = mode;
    state.headerLastTier = tier;

    return true;
  }

  function footerRoots() {
    const roots = [
      ...document.querySelectorAll(
        '[data-nmr-footer], [data-nmr-footer-payments]'
      )
    ];

    return roots.filter(
      (node, index) =>
        roots.indexOf(node) ===
        index
    );
  }

  function footerImageCandidates() {
    const mode =
      effectiveHeaderMode();

    const roots =
      footerRoots();

    const seen =
      new Set();

    const candidates =
      [];

    for (const root of roots) {
      const images =
        root.querySelectorAll(
          'img'
        );

      for (const image of images) {
        if (
          image.classList.contains(
            'nmr-mode-logo'
          )
        ) {
          const activeClass =
            'nmr-mode-logo--' +
            mode;

          if (
            !image.classList.contains(
              activeClass
            )
          ) {
            continue;
          }
        }

        const src =
          imageUrl(image);

        if (
          !src ||
          seen.has(src)
        ) {
          continue;
        }

        seen.add(src);

        const priority =
          image.closest(
            '.nmr-footer__logo'
          )
            ? 0
            : image.classList.contains(
                'nmr-footer__trust-image'
              )
              ? 1
              : image.closest(
                  '.nmr-footer__apps'
                )
                ? 2
                : image.closest(
                    '[data-nmr-footer-payments], [data-nmr-payment-strip]'
                  )
                  ? 3
                  : 4;

        candidates.push({
          image,
          src,
          priority
        });
      }
    }

    candidates.sort(
      (a, b) =>
        a.priority -
        b.priority
    );

    return candidates;
  }

  function warmFooterImages(
    limit = 0
  ) {
    if (
      limit <= 0 ||
      !heavyWarmAllowed()
    ) {
      return 0;
    }

    let count = 0;

    for (
      const candidate of
      footerImageCandidates()
    ) {
      if (count >= limit) {
        break;
      }

      const url =
        normalizeAssetUrl(
          candidate.src
        );

      if (
        !url ||
        state.footerImagesRequested.has(
          url.href
        )
      ) {
        continue;
      }

      state.footerImagesRequested.add(
        url.href
      );

      /*
        Existing lazy image gets permission to load because
        the footer is now near the viewport. Keep browser sizing/srcset.
      */
      try {
        candidate.image.loading =
          'eager';

        candidate.image.fetchPriority =
          'low';

        candidate.image.decode?.()
          .then(() => {
            state.decodedImages++;
            state.footerDecodedImages++;
          })
          .catch(() => {});
      }
      catch(_error) {}

      /*
        Keep a hot reference too; this is useful when the browser
        has not selected currentSrc yet.
      */
      retainImage(
        url.href
      );

      count++;
    }

    return count;
  }

  function touchFooterSurfaces(
    limit = 0
  ) {
    if (
      limit <= 0 ||
      !heavyWarmAllowed()
    ) {
      return 0;
    }

    const selectors = [
      '[data-nmr-footer] .nmr-footer__main',
      '[data-nmr-footer-payments] .nmr-footer__bottom'
    ];

    let count = 0;

    for (
      const selector of selectors
    ) {
      if (count >= limit) {
        break;
      }

      const node =
        document.querySelector(
          selector
        );

      if (!node) {
        continue;
      }

      try {
        const style =
          getComputedStyle(node);

        style.backgroundColor;
        style.color;
        style.borderRadius;

        count++;
      }
      catch(_error) {}
    }

    state.footerSurfacesTouched +=
      count;

    return count;
  }

  function warmFooter(
    reason = 'near-viewport'
  ) {
    if (
      !heavyWarmAllowed()
    ) {
      return blockLifecycle(
        'footer-before-ready'
      );
    }

    const roots =
      footerRoots();

    if (!roots.length) {
      return false;
    }

    const tier =
      computeTier();

    const policy =
      TIER_POLICIES[tier] ||
      TIER_POLICIES.standard;

    state.hotFooter =
      roots[0];

    state.footerWarmRuns++;

    if (
      policy.networkWarm &&
      policy.footerImageLimit > 0
    ) {
      warmFooterImages(
        policy.footerImageLimit
      );
    }

    touchFooterSurfaces(
      policy.footerSurfaceLimit
    );

    for (const root of roots) {
      try {
        root.dataset.nmrFooterWarm =
          policy.footerImageLimit > 0
            ? 'true'
            : 'local';

        root.dataset.nmrFooterWarmTier =
          tier;
      }
      catch(_error) {}
    }

    state.footerWarmReady =
      true;

    state.footerWarmAt =
      Date.now();

    state.footerWarmReason =
      reason;

    if (state.footerObserver) {
      state.footerObserver.disconnect();
      state.footerObserver = null;
    }

    return true;
  }

  function footerNearViewport(
    margin = 0
  ) {
    const roots =
      footerRoots();

    if (!roots.length) {
      return false;
    }

    const viewport =
      window.innerHeight ||
      document.documentElement.clientHeight ||
      0;

    return roots.some(root => {
      const rect =
        root.getBoundingClientRect();

      return (
        rect.top <=
          viewport + margin &&
        rect.bottom >=
          -margin
      );
    });
  }

  function footerFallbackCheck() {
    if (
      !heavyWarmAllowed()
    ) {
      return;
    }

    const policy =
      tierPolicy();

    const margin =
      policy.footerRootMargin || 0;

    if (
      footerNearViewport(
        margin
      )
    ) {
      warmFooter(
        'fallback-near-viewport'
      );

      window.removeEventListener(
        'scroll',
        footerFallbackCheck
      );

      window.removeEventListener(
        'resize',
        footerFallbackCheck
      );

      state.footerFallbackBound =
        false;
    }
  }

  function installFooterObserver() {
    if (
      state.footerObserved ||
      !heavyWarmAllowed()
    ) {
      return false;
    }

    const roots =
      footerRoots();

    if (!roots.length) {
      return false;
    }

    state.footerObserved =
      true;

    const policy =
      tierPolicy();

    const margin =
      policy.footerRootMargin || 0;

    state.footerObserverMargin =
      margin + 'px 0px';

    if (
      margin <= 0
    ) {
      /*
        Constrained tier keeps footer lazy.
        Existing browser lazy-loading remains the only loader.
      */
      return true;
    }

    if (
      'IntersectionObserver' in
      window
    ) {
      state.footerObserver =
        new IntersectionObserver(
          entries => {
            if (
              entries.some(
                entry =>
                  entry.isIntersecting
              )
            ) {
              warmFooter(
                'intersection'
              );
            }
          },
          {
            root: null,
            rootMargin:
              state.footerObserverMargin,
            threshold: 0.01
          }
        );

      for (const root of roots) {
        state.footerObserver.observe(
          root
        );
      }

      return true;
    }

    if (
      !state.footerFallbackBound
    ) {
      state.footerFallbackBound =
        true;

      window.addEventListener(
        'scroll',
        footerFallbackCheck,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        footerFallbackCheck,
        { passive: true }
      );

      footerFallbackCheck();
    }

    return true;
  }

  function warmShell(
    reason = 'idle'
  ) {
    if (
      !heavyWarmAllowed()
    ) {
      return blockLifecycle(
        'shell-before-ready'
      );
    }

    state.shellRuns++;

    /*
      PHASE 4:
      Header gets a dedicated warm pipeline.
      Footer eager warming is intentionally disabled until Phase 6.
    */
    warmHeader(
      'shell:' + reason
    );

    const footer =
      document.querySelector(
        '.nmr-footer'
      ) ||
      document.querySelector(
        'footer'
      );

    state.hotFooter =
      footer || null;

    warmAccount(
      'shell:' + reason
    );

    return true;
  }

  function safeAnchor(target) {
    return target?.closest?.(
      'a[href]'
    ) || null;
  }

  function headerAccountTarget(target) {
    return target?.closest?.(
      '[data-nmr-header-account]'
    ) || null;
  }

  function searchTarget(target) {
    return target?.closest?.(
      '[data-nmr-layer-trigger="search"]'
    ) || null;
  }

  function stage01Budget() {
    const policy =
      tierPolicy();

    return (
      policy.stage01Budget || 0
    );
  }

  function stage01ResolvedUrl(rawUrl) {
    const url =
      new URL(
        rawUrl,
        window.location.origin
      );

    const current =
      new URL(
        window.location.href
      );

    const previewThemeId =
      current.searchParams.get(
        'preview_theme_id'
      );

    if (
      previewThemeId &&
      !url.searchParams.has(
        'preview_theme_id'
      )
    ) {
      url.searchParams.set(
        'preview_theme_id',
        previewThemeId
      );
    }

    url.hash = '';

    return url;
  }

  function normalizeStage01Signal(
    signal = false
  ) {
    if (
      typeof signal === 'string' &&
      signal
    ) {
      return signal;
    }

    state.stage01LegacyBooleanCalls++;

    return signal === true
      ? 'stage01-strong'
      : 'dwell';
  }

  function stage01Allowed(rawUrl) {
    if (
      !rawUrl ||
      document.visibilityState ===
        'hidden'
    ) {
      return false;
    }

    const activeConnection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (
      activeConnection?.saveData ||
      ['slow-2g', '2g'].includes(
        activeConnection?.effectiveType
      )
    ) {
      return false;
    }

    let url;

    try {
      url =
        stage01ResolvedUrl(
          rawUrl
        );
    }
    catch(_error) {
      return false;
    }

    const route =
      classifyRoute(
        url,
        {
          source: 'stage01'
        }
      );

    return Boolean(
      route.allowPrefetch
    );
  }

  function stage01Prefetch(
    rawUrl,
    signal = false
  ) {
    state.stage01UnifiedCalls++;

    if (
      !heavyWarmAllowed()
    ) {
      state.stage01UnifiedNoWarmDecisions++;

      return blockLifecycle(
        'stage01-before-ready'
      );
    }

    if (!stage01Allowed(rawUrl)) {
      state.stage01UnifiedNoWarmDecisions++;

      return false;
    }

    const reason =
      normalizeStage01Signal(
        signal
      );

    const resolved =
      stage01ResolvedUrl(
        rawUrl
      );

    const route =
      classifyRoute(
        resolved,
        {
          record: false,
          source:
            'stage01-unified'
        }
      );

    if (
      !route.allowPrefetch ||
      !route.url ||
      sameDocument(
        route.url
      )
    ) {
      state.stage01UnifiedNoWarmDecisions++;

      return false;
    }

    const url =
      route.url.toString();

    if (
      stage01Prefetched.has(
        url
      )
    ) {
      return true;
    }

    if (
      stage01Prefetched.size >=
        stage01Budget()
    ) {
      state.stage01UnifiedNoWarmDecisions++;

      return false;
    }

    const tier =
      computeTier();

    const policy =
      TIER_POLICIES[tier] ||
      TIER_POLICIES.standard;

    if (
      !policy.networkWarm
    ) {
      state.stage01UnifiedNoWarmDecisions++;

      return false;
    }

    const confidence =
      intentConfidence(
        reason
      );

    const navDecision =
      navigationPolicyDecision(
        route,
        tier,
        confidence,
        reason
      );

    if (
      navDecision.mode ===
        'none'
    ) {
      state.stage01UnifiedNoWarmDecisions++;

      return false;
    }

    if (
      navDecision.mode ===
        'prerender'
    ) {
      state.stage01UnifiedPrerenderDecisions++;
    }
    else {
      state.stage01UnifiedPrefetchDecisions++;
    }

    const installed =
      installSpeculation(
        route.url,
        navDecision.mode,
        {
          highConfidence:
            confidence === 'high',
          source:
            'stage01:' + reason,
          eagerness:
            navDecision.eagerness ||
            'moderate'
        }
      );

    if (installed) {
      stage01Prefetched.add(
        url
      );
    }

    return installed;
  }

  function stage01Schedule(anchor) {
    if (
      !anchor ||
      stage01IntentTimers.has(
        anchor
      )
    ) {
      return;
    }

    state.intentScheduled++;

    const timer =
      window.setTimeout(
        () => {
          stage01IntentTimers.delete(
            anchor
          );

          stage01IntentAnchors.delete(
            anchor
          );

          state.lastIntentConfidence =
            'medium';

          state.intentExecuted++;
          state.intentMediumConfidence++;

          stage01Prefetch(
            anchor.href,
            'dwell'
          );
        },
        state.intentDwellMs
      );

    stage01IntentTimers.set(
      anchor,
      timer
    );

    stage01IntentAnchors.add(
      anchor
    );
  }

  function stage01Cancel(anchor) {
    const timer =
      stage01IntentTimers.get(
        anchor
      );

    if (!timer) {
      return false;
    }

    window.clearTimeout(
      timer
    );

    stage01IntentTimers.delete(
      anchor
    );

    stage01IntentAnchors.delete(
      anchor
    );

    state.intentCancelled++;

    return true;
  }

  function stage01InsideOwnedShell(anchor) {
    return Boolean(
      anchor?.closest?.(
        '[data-nmr-header], .nmr-footer, footer'
      )
    );
  }

  function bindStage01Predictor() {
    if (stage01PredictorBound) {
      return;
    }

    stage01PredictorBound =
      true;

    document.addEventListener(
      'pointerover',
      event => {
        const anchor =
          event.target?.closest?.(
            'a[href]'
          );

        if (
          !anchor ||
          stage01InsideOwnedShell(
            anchor
          )
        ) {
          return;
        }

        if (
          stage01Allowed(
            anchor.href
          )
        ) {
          stage01Schedule(
            anchor
          );
        }
      },
      { passive: true }
    );

    document.addEventListener(
      'pointerout',
      event => {
        const anchor =
          event.target?.closest?.(
            'a[href]'
          );

        if (!anchor) {
          return;
        }

        const related =
          event.relatedTarget;

        if (
          related instanceof Node &&
          anchor.contains(
            related
          )
        ) {
          return;
        }

        stage01Cancel(
          anchor
        );
      },
      { passive: true }
    );

    document.addEventListener(
      'pointerdown',
      event => {
        const anchor =
          event.target?.closest?.(
            'a[href]'
          );

        if (
          !anchor ||
          stage01InsideOwnedShell(
            anchor
          )
        ) {
          return;
        }

        stage01Cancel(
          anchor
        );

        const pointerType =
          String(
            event.pointerType ||
            'mouse'
          );

        const signal =
          pointerType === 'touch'
            ? 'pointerdown-touch'
            : pointerType === 'pen'
              ? 'pointerdown-pen'
              : 'pointerdown-mouse';

        if (
          duplicateTouchSignal(
            anchor,
            signal
          )
        ) {
          return;
        }

        const highConfidence =
          pointerType !== 'touch';

        state.lastIntentConfidence =
          highConfidence
            ? 'high'
            : 'medium';

        state.intentExecuted++;

        if (highConfidence) {
          state.intentHighConfidence++;
        }
        else {
          state.intentMediumConfidence++;
        }

        stage01Prefetch(
          anchor.href,
          signal
        );
      },
      { passive: true }
    );

    document.addEventListener(
      'focusin',
      event => {
        const anchor =
          event.target?.closest?.(
            'a[href]'
          );

        if (
          !anchor ||
          stage01InsideOwnedShell(
            anchor
          )
        ) {
          return;
        }

        stage01Cancel(
          anchor
        );

        state.lastIntentConfidence =
          'medium';

        state.intentExecuted++;
        state.intentMediumConfidence++;

        stage01Prefetch(
          anchor.href,
          'focusin'
        );
      }
    );

    document.addEventListener(
      'touchstart',
      event => {
        const anchor =
          event.target?.closest?.(
            'a[href]'
          );

        if (
          !anchor ||
          stage01InsideOwnedShell(
            anchor
          )
        ) {
          return;
        }

        stage01Cancel(
          anchor
        );

        if (
          duplicateTouchSignal(
            anchor,
            'touchstart'
          )
        ) {
          return;
        }

        state.lastIntentConfidence =
          'medium';

        state.intentExecuted++;
        state.intentMediumConfidence++;

        stage01Prefetch(
          anchor.href,
          'touchstart'
        );
      },
      { passive: true }
    );
  }

  function duplicateTouchSignal(
    owner,
    signal
  ) {
    if (!owner) {
      return false;
    }

    const isTouchFamily =
      signal === 'touchstart' ||
      signal === 'pointerdown-touch';

    if (!isTouchFamily) {
      return false;
    }

    const now =
      Date.now();

    const previous =
      state.intentRecentSignals.get(
        owner
      );

    if (
      previous &&
      previous.touch === true &&
      now - previous.at < 350
    ) {
      return true;
    }

    state.intentRecentSignals.set(
      owner,
      {
        touch: true,
        signal,
        at: now
      }
    );

    return false;
  }

  function shellIntentOwner(target) {
    return (
      headerAccountTarget(
        target
      ) ||
      searchTarget(
        target
      ) ||
      safeAnchor(
        target
      )
    );
  }

  function cancelShellIntent(owner) {
    if (!owner) {
      return false;
    }

    const timer =
      state.shellIntentTimers.get(
        owner
      );

    if (!timer) {
      return false;
    }

    window.clearTimeout(
      timer
    );

    state.shellIntentTimers.delete(
      owner
    );

    state.shellIntentOwners.delete(
      owner
    );

    state.intentCancelled++;

    return true;
  }

  function primeSearchIntent() {
    if (
      !heavyWarmAllowed()
    ) {
      return false;
    }

    const layer =
      document.querySelector(
        '[data-nmr-layer="search"]'
      );

    try {
      if (layer) {
        getComputedStyle(
          layer
        ).visibility;
      }
    }
    catch(_error) {}

    return Boolean(layer);
  }

  function executeShellIntent(
    owner,
    signal = 'dwell',
    event = null
  ) {
    if (!owner) {
      return false;
    }

    if (
      duplicateTouchSignal(
        owner,
        signal
      )
    ) {
      return false;
    }

    if (
      headerAccountTarget(
        owner
      )
    ) {
      warmAccount(
        signal === 'dwell'
          ? 'account-intent-dwell'
          : 'account-intent-' +
            signal
      );

      state.lastIntent =
        'account';

      state.lastIntentAt =
        Date.now();

      state.lastIntentConfidence =
        signal === 'pointerdown-mouse' ||
        signal === 'pointerdown-pen'
          ? 'high'
          : 'medium';

      state.intentExecuted++;

      if (
        state.lastIntentConfidence ===
        'high'
      ) {
        state.intentHighConfidence++;
      }
      else {
        state.intentMediumConfidence++;
      }

      return true;
    }

    if (
      searchTarget(
        owner
      )
    ) {
      primeSearchIntent();

      state.lastIntent =
        'search';

      state.lastIntentAt =
        Date.now();

      state.lastIntentConfidence =
        signal === 'pointerdown-mouse' ||
        signal === 'pointerdown-pen'
          ? 'high'
          : 'medium';

      state.intentExecuted++;

      if (
        state.lastIntentConfidence ===
        'high'
      ) {
        state.intentHighConfidence++;
      }
      else {
        state.intentMediumConfidence++;
      }

      return true;
    }

    const anchor =
      owner.matches?.(
        'a[href]'
      )
        ? owner
        : safeAnchor(
            owner
          );

    if (!anchor) {
      return false;
    }

    return warmUrl(
      anchor.href,
      'intent-' + signal
    );
  }

  function scheduleShellIntent(
    owner,
    event = null
  ) {
    if (
      !owner ||
      state.shellIntentTimers.has(
        owner
      )
    ) {
      return;
    }

    state.intentScheduled++;

    const timer =
      window.setTimeout(
        () => {
          state.shellIntentTimers.delete(
            owner
          );

          state.shellIntentOwners.delete(
            owner
          );

          executeShellIntent(
            owner,
            'dwell',
            event
          );
        },
        state.intentDwellMs
      );

    state.shellIntentTimers.set(
      owner,
      timer
    );

    state.shellIntentOwners.add(
      owner
    );
  }

  function handleIntent(event) {
    const target =
      event.target;

    if (
      heavyWarmAllowed() &&
      target?.closest?.(
        '[data-nmr-header]'
      )
    ) {
      warmHeader(
        'header-intent'
      );
    }

    const owner =
      shellIntentOwner(
        target
      );

    if (!owner) {
      return;
    }

    if (
      event.type ===
        'pointerover'
    ) {
      scheduleShellIntent(
        owner,
        event
      );

      return;
    }

    cancelShellIntent(
      owner
    );

    if (
      event.type ===
        'pointerdown'
    ) {
      const pointerType =
        String(
          event.pointerType ||
          'mouse'
        );

      const signal =
        pointerType === 'touch'
          ? 'pointerdown-touch'
          : pointerType === 'pen'
            ? 'pointerdown-pen'
            : 'pointerdown-mouse';

      executeShellIntent(
        owner,
        signal,
        event
      );

      return;
    }

    if (
      event.type ===
        'touchstart'
    ) {
      executeShellIntent(
        owner,
        'touchstart',
        event
      );

      return;
    }

    if (
      event.type ===
        'focusin'
    ) {
      executeShellIntent(
        owner,
        'focusin',
        event
      );
    }
  }

  function handleIntentOut(event) {
    const owner =
      shellIntentOwner(
        event.target
      );

    if (!owner) {
      return;
    }

    const related =
      event.relatedTarget;

    if (
      related instanceof Node &&
      owner.contains(
        related
      )
    ) {
      return;
    }

    cancelShellIntent(
      owner
    );
  }

  function bindIntent() {
    const shell =
      document.querySelector(
        '[data-nmr-header]'
      ) ||
      document.body;

    shell.addEventListener(
      'pointerover',
      handleIntent,
      {
        passive: true,
        capture: true
      }
    );

    shell.addEventListener(
      'pointerout',
      handleIntentOut,
      {
        passive: true,
        capture: true
      }
    );

    shell.addEventListener(
      'pointerdown',
      handleIntent,
      {
        passive: true,
        capture: true
      }
    );

    shell.addEventListener(
      'focusin',
      handleIntent,
      true
    );

    shell.addEventListener(
      'touchstart',
      handleIntent,
      {
        passive: true,
        capture: true
      }
    );

    const footer =
      document.querySelector(
        '.nmr-footer'
      ) ||
      document.querySelector(
        'footer'
      );

    footer?.addEventListener(
      'pointerover',
      event => {
        if (
          heavyWarmAllowed()
        ) {
          warmFooter(
            'footer-intent'
          );
        }

        handleIntent(
          event
        );
      },
      {
        passive: true,
        capture: true
      }
    );

    footer?.addEventListener(
      'pointerout',
      handleIntentOut,
      {
        passive: true,
        capture: true
      }
    );

    footer?.addEventListener(
      'pointerdown',
      event => {
        if (
          heavyWarmAllowed()
        ) {
          warmFooter(
            'footer-pointerdown'
          );
        }

        handleIntent(
          event
        );
      },
      {
        passive: true,
        capture: true
      }
    );

    footer?.addEventListener(
      'focusin',
      handleIntent,
      true
    );

    footer?.addEventListener(
      'touchstart',
      event => {
        if (
          heavyWarmAllowed()
        ) {
          warmFooter(
            'footer-touch-intent'
          );
        }

        handleIntent(
          event
        );
      },
      {
        passive: true,
        capture: true
      }
    );
  }

  function initialIdleWarm() {
    idle(() => {
      if (
        !heavyWarmAllowed()
      ) {
        blockLifecycle(
          'idle-1-not-ready'
        );

        return;
      }

      state.idleRuns++;

      warmShell('idle-1');

      idle(() => {
        if (
          !heavyWarmAllowed()
        ) {
          blockLifecycle(
            'idle-2-not-ready'
          );

          return;
        }

        state.idleRuns++;

        /*
          Second pass catches app-block DOM injected after the
          initial storefront render.
        */
        warmAccount('idle-2');
      }, 2200);
    }, 900);
  }

  function warm(
    reason = 'manual'
  ) {
    if (
      !heavyWarmAllowed()
    ) {
      return blockLifecycle(
        'manual-before-ready'
      );
    }

    warmShell(reason);
    watchAccountRoot();

    return true;
  }

  function metrics() {
    refreshNavigationRuntimeMetrics(
      'metrics-read'
    );

    return {
      version: VERSION,
      qa:
        FINAL_QA_PROFILE,
      uptime:
        Date.now() - state.startedAt,
      tier:
        state.tier,
      tierReason:
        state.tierReason,
      capabilityEvaluations:
        state.capabilityEvaluations,
      capabilitySignals:
        state.capabilitySignals,
      capabilityPolicy:
        TIER_POLICIES[state.tier] ||
        TIER_POLICIES.standard,
      footer:
        {
          warmRuns:
            state.footerWarmRuns,
          ready:
            state.footerWarmReady,
          warmedAt:
            state.footerWarmAt,
          reason:
            state.footerWarmReason,
          decodedImages:
            state.footerDecodedImages,
          imagesRequested:
            Array.from(
              state.footerImagesRequested
            ),
          surfacesTouched:
            state.footerSurfacesTouched,
          observed:
            state.footerObserved,
          observerMargin:
            state.footerObserverMargin,
          observerActive:
            Boolean(
              state.footerObserver
            )
        },
      header:
        {
          warmRuns:
            state.headerWarmRuns,
          ready:
            state.headerWarmReady,
          warmedAt:
            state.headerWarmAt,
          reason:
            state.headerWarmReason,
          mode:
            state.headerLastMode,
          tier:
            state.headerLastTier,
          decodedImages:
            state.headerDecodedImages,
          styleAssets:
            Array.from(
              state.headerStyleAssets
            ),
          surfacesTouched:
            state.headerSurfacesTouched
        },
      lifecycle:
        {
          pageLoaded:
            state.pageLoaded,
          loadAt:
            state.loadAt,
          prerendering:
            pageIsPrerendering(),
          activated:
            state.activated,
          activationAt:
            state.activationAt,
          interactiveBound:
            state.interactiveBound,
          heavyWarmAllowed:
            heavyWarmAllowed(),
          heavyWarmStarted:
            state.heavyWarmStarted,
          heavyWarmAt:
            state.heavyWarmAt,
          heavyWarmReason:
            state.heavyWarmReason,
          lifecycleBlocks:
            state.lifecycleBlocks,
          paused:
            state.lifecyclePaused,
          pauses:
            state.lifecyclePauses,
          resumes:
            state.lifecycleResumes,
          lightRefreshes:
            state.lightRefreshes,
          bfcacheLightRefreshes:
            state.bfcacheLightRefreshes,
          visibilityHiddenCount:
            state.visibilityHiddenCount,
          visibilityVisibleCount:
            state.visibilityVisibleCount,
          visibilityCooldownMs:
            state.visibilityCooldownMs,
          visibilityCooldownSkips:
            state.visibilityCooldownSkips,
          hiddenIntentCancellations:
            state.hiddenIntentCancellations,
          hiddenSpeculationStops:
            state.hiddenSpeculationStops,
          pagehideCount:
            state.pagehideCount,
          bfcachePagehides:
            state.bfcachePagehides,
          lastPauseAt:
            state.lastPauseAt,
          lastPauseReason:
            state.lastPauseReason,
          lastResumeAt:
            state.lastResumeAt,
          lastResumeReason:
            state.lastResumeReason,
          pendingStage01Intents:
            stage01IntentAnchors.size,
          pendingShellIntents:
            state.shellIntentOwners.size
        },
      saveData:
        Boolean(connection?.saveData),
      effectiveType:
        connection?.effectiveType || '',
      deviceMemory:
        navigator.deviceMemory || null,
      hardwareConcurrency:
        navigator.hardwareConcurrency || null,
      idleRuns:
        state.idleRuns,
      shellRuns:
        state.shellRuns,
      account:
        {
          runs:
            state.accountRuns,
          ready:
            state.accountReady,
          secondaryRequested:
            state.secondaryRequested,
          secondaryArmed:
            state.accountSecondaryArmed,
          warmRequests:
            state.accountWarmRequests,
          successes:
            state.accountWarmSuccesses,
          failures:
            state.accountWarmFailures,
          rewarms:
            state.accountRewarms,
          rewarmsInWindow:
            state.accountRewarmsInWindow,
          lastRequestAt:
            state.accountLastRequestAt,
          lastSuccessAt:
            state.accountLastSuccessAt,
          lastReason:
            state.accountLastReason,
          lastSignature:
            state.accountLastSignature
        },
      accountRuns:
        state.accountRuns,
      accountReady:
        state.accountReady,
      secondaryRequested:
        state.secondaryRequested,
      decodedImages:
        state.decodedImages,
      retainedImages:
        state.retainedImages.length,
      speculationRequests:
        {
          requestedPrefetches:
            state.prefetchRequests,
          requestedPrerenders:
            state.prerenderRequests,
          prefetchHintsInserted:
            state.prefetchLinksInserted,
          prefetchFetchRequests:
            state.prefetchFetchRequests,
          prerenderRulesInserted:
            state.prerenderRulesInserted,
          requestedPrefetchUrls:
            Array.from(
              state.prefetched
            ),
          requestedPrerenderUrls:
            Array.from(
              state.prerendered
            ),
          prefetchCompletionObservable:
            false,
          note:
            'Prefetch request is observable; browser completion is not asserted.'
        },
      prefetched:
        Array.from(state.prefetched),
      prerendered:
        Array.from(state.prerendered),
      navigationRuntime:
        {
          supported:
            state.navigationMetricsSupported,
          entryType:
            state.navigationEntryType,
          activationStart:
            state.navigationActivationStart,
          activatedFromPrerender:
            state.activatedFromPrerender,
          wasPrerenderingAtBoot:
            state.wasPrerenderingAtBoot,
          observedPrerenderActivations:
            state.prerenderActivationsObserved,
          duration:
            state.navigationDuration,
          responseEnd:
            state.navigationResponseEnd,
          domContentLoadedEventEnd:
            state.navigationDomContentLoadedEnd,
          loadEventEnd:
            state.navigationLoadEventEnd,
          pageshowCount:
            state.pageshowCount,
          bfcacheRestores:
            state.bfcacheRestores,
          lastReadReason:
            state.navigationMetricLastReason,
          lastReadAt:
            state.navigationMetricLastReadAt,
          performanceReads:
            state.performanceReads
        },
      navigationPolicy:
        {
          decisions:
            state.policyDecisions,
          prefetch:
            state.policyPrefetchDecisions,
          prerender:
            state.policyPrerenderDecisions,
          noWarm:
            state.policyNoWarmDecisions,
          downgrades:
            state.policyDowngrades,
          unsupportedFallbacks:
            state.policyUnsupportedFallbacks,
          families:
            {
              ...state.policyFamilyCounts
            },
          last:
            state.lastNavigationPolicy
        },
      budgets:
        {
          policy:
            currentBudgetPolicy(),
          requestedPrefetches:
            state.prefetchRequests,
          requestedPrerenders:
            state.prerenderRequests,
          prefetchRequests:
            state.prefetchRequests,
          prerenderRequests:
            state.prerenderRequests,
          prefetchRemaining:
            Math.max(
              0,
              currentBudgetPolicy()
                .prefetchBudget -
              state.prefetchRequests
            ),
          prerenderRemaining:
            Math.max(
              0,
              currentBudgetPolicy()
                .prerenderBudget -
              state.prerenderRequests
            ),
          activePrerender:
            state.activePrerenderUrl,
          prefetchRejected:
            state.prefetchBudgetRejected,
          prerenderRejected:
            state.prerenderBudgetRejected,
          cooldownRejected:
            state.cooldownRejected,
          prerenderReplacements:
            state.prerenderReplacements,
          warmedUrls:
            Array.from(
              state.prefetchBudgetUrls
            ),
          retainedImageLimit:
            currentBudgetPolicy()
              .retainedImageLimit
        },
      routes:
        {
          checks:
            state.routeChecks,
          blocked:
            state.routeBlocked,
          prefetchOnly:
            state.routePrefetchOnly,
          prerenderSafe:
            state.routePrerenderSafe,
          unknownBlocked:
            state.routeUnknownBlocked,
          localizedChecks:
            state.routeLocalizedChecks,
          blockedSamples:
            [...state.routeBlockedSamples],
          lastDecision:
            state.lastRouteDecision
        },
      intent:
        {
          dwellMs:
            state.intentDwellMs,
          scheduled:
            state.intentScheduled,
          executed:
            state.intentExecuted,
          cancelled:
            state.intentCancelled,
          highConfidence:
            state.intentHighConfidence,
          mediumConfidence:
            state.intentMediumConfidence,
          last:
            state.lastIntent,
          lastAt:
            state.lastIntentAt,
          lastConfidence:
            state.lastIntentConfidence
        },
      lastIntent:
        state.lastIntent,
      lastIntentAt:
        state.lastIntentAt,
      skipped:
        [...state.skipped],
      stage01Unified:
        {
          calls:
            state.stage01UnifiedCalls,
          prefetchDecisions:
            state.stage01UnifiedPrefetchDecisions,
          prerenderDecisions:
            state.stage01UnifiedPrerenderDecisions,
          noWarmDecisions:
            state.stage01UnifiedNoWarmDecisions,
          legacyBooleanCalls:
            state.stage01LegacyBooleanCalls,
          warmedUrls:
            Array.from(
              stage01Prefetched
            ),
          directFetchPath:
            false,
          directPrefetchLinkPath:
            false
        },
      stage01Prefetched:
        Array.from(
          stage01Prefetched
        ),
      stage01PredictorBound:
        stage01PredictorBound
    };
  }

  const api =
    Object.freeze({
      version: VERSION,
      warm,
      warmAccount,
      warmHeader,
      warmFooter,
      warmShell,
      warmUrl,
      classifyRoute,
      navigationPolicyDecision,
      refreshNavigationRuntimeMetrics,
      lightResumeLifecycle,
      pauseLifecycleForHidden,
      stage01Prefetch,
      metrics
    });

  window[KEY] =
    api;

  window.NMRPerformanceBrainV2 =
    api;

  window.NMRPerformanceBrain =
    api;

  /*
    Compatibility alias:
    Existing diagnostics can keep using NMRWarmupEngine.metrics().
  */
  window.NMRWarmupEngine =
    api;

  computeTier();

  refreshNavigationRuntimeMetrics(
    'boot'
  );

  /*
    PHASE 3 LIFECYCLE CONTRACT

    Prediction listeners may bind after DOM is ready.
    Heavy warmup waits for window.load and real activation.
  */
  if (
    document.readyState ===
      'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        bindInteractiveOnce();
      },
      { once: true }
    );
  }
  else {
    bindInteractiveOnce();
  }

  if (
    document.readyState ===
      'complete'
  ) {
    state.pageLoaded =
      true;

    if (!state.loadAt) {
      state.loadAt =
        Date.now();
    }
  }
  else {
    window.addEventListener(
      'load',
      () => {
        markPageLoaded(
          'window-load'
        );
      },
      { once: true }
    );
  }

  if (
    pageIsPrerendering()
  ) {
    state.prerendering =
      true;

    state.activated =
      false;

    document.addEventListener(
      'prerenderingchange',
      () => {
        markActivated(
          'prerenderingchange'
        );
      },
      { once: true }
    );
  }
  else {
    state.prerendering =
      false;

    state.activated =
      true;

    if (!state.activationAt) {
      state.activationAt =
        Date.now();
    }
  }

  /*
    Start immediately only when both gates were already satisfied
    before this deferred script ran. Otherwise load/activation
    listeners above own the transition.
  */
  startHeavyWarmLifecycle(
    'boot-ready'
  );

  if (
    connection &&
    typeof connection.addEventListener ===
      'function'
  ) {
    connection.addEventListener(
      'change',
      () => {
        computeTier();
      },
      { passive: true }
    );
  }

  window.addEventListener(
    'pagehide',
    event => {
      state.pagehideCount++;

      if (event.persisted) {
        state.bfcachePagehides++;
      }

      pauseLifecycleForHidden(
        event.persisted
          ? 'pagehide-bfcache'
          : 'pagehide'
      );
    },
    { passive: true }
  );

  window.addEventListener(
    'pageshow',
    event => {
      state.pageshowCount++;

      if (event.persisted) {
        state.bfcacheRestores++;
      }

      refreshNavigationRuntimeMetrics(
        event.persisted
          ? 'pageshow-bfcache'
          : 'pageshow'
      );

      if (event.persisted) {
        lightResumeLifecycle(
          'bfcache',
          {
            force: true
          }
        );
      }
    },
    { passive: true }
  );

  document.addEventListener(
    'visibilitychange',
    () => {
      if (
        document.visibilityState ===
          'hidden'
      ) {
        state.visibilityHiddenCount++;

        pauseLifecycleForHidden(
          'visibility-hidden'
        );

        return;
      }

      if (
        document.visibilityState ===
          'visible'
      ) {
        state.visibilityVisibleCount++;

        lightResumeLifecycle(
          'visibility-visible'
        );
      }
    },
    { passive: true }
  );
})();